import type { Listing } from "@/data/listings";

import { useCallback, useEffect, useRef, useState } from "react";

import { fetchListings } from "./listings";

type UseListingsOptions = {
  page?: number;
  limit?: number;
  city?: string;
  subject?: string;
  ownerId?: string;
  featured?: boolean;
  isOnline?: boolean;
  minPrice?: number;
  maxPrice?: number;
  minRating?: number;
  search?: string;
  sortBy?:
    | "relevance"
    | "price"
    | "rating"
    | "newest"
    | "reviews"
    | "featuredRank";
  sortDir?: "asc" | "desc";
  mine?: boolean;
  status?: "draft" | "active";
  /**
   * Résultat déjà connu au premier rendu, fourni par le rendu serveur.
   *
   * Sert d'état initial pour que le HTML serveur contienne les annonces. Le
   * client refait la requête ensuite, mais sans repasser par un état vide : la
   * liste ne disparaît donc pas entre l'affichage initial et l'hydratation.
   *
   * À ne fournir que si les filtres correspondent à ceux du rendu serveur,
   * sinon la première peinture montrerait des annonces sans rapport.
   */
  initialData?: ListingResult | null;
};

type ListingResult = {
  items: Listing[];
  total: number;
  page: number;
  limit: number;
};

export function useListings(options?: UseListingsOptions) {
  const initialData = options?.initialData ?? null;
  // Conservé dans une ref : seul le tout premier passage de l'effet doit
  // épargner l'état initial. Tout changement de filtre ensuite repart d'une
  // liste vide, comme auparavant.
  const hasSeededRef = useRef(Boolean(initialData));
  const [data, setData] = useState<ListingResult | null>(initialData);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const page = options?.page ?? 1;
  const limit = options?.limit ?? 15;
  const city = options?.city;
  const subject = options?.subject;
  const ownerId = options?.ownerId;
  const featured = options?.featured;
  const isOnline = options?.isOnline;
  const minPrice = options?.minPrice;
  const maxPrice = options?.maxPrice;
  const minRating = options?.minRating;
  const search = options?.search;
  const sortBy = options?.sortBy;
  const sortDir = options?.sortDir;
  const mine = options?.mine;
  const status = options?.status;

  const refetch = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (hasSeededRef.current) {
      hasSeededRef.current = false;
    } else {
      setLoading(true);
      setData(null);
    }

    fetchListings({
      page,
      limit,
      city,
      subject,
      ownerId,
      featured,
      isOnline,
      minPrice,
      maxPrice,
      minRating,
      search,
      sortBy,
      sortDir,
      mine,
      status,
    })
      .then((listings) => {
        if (!isMounted) return;
        setData(listings);
        setError(null);
      })
      .catch(() => {
        if (!isMounted) return;
        setData(null);
        setError("Impossible de charger les annonces.");
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [
    city,
    featured,
    isOnline,
    limit,
    maxPrice,
    minPrice,
    minRating,
    ownerId,
    page,
    refreshKey,
    search,
    sortBy,
    sortDir,
    mine,
    status,
    subject,
  ]);

  return { data, loading, error, refetch };
}
